import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Eye, Network } from "lucide-react";
import { useTableSort, SortTh } from "@/lib/tableSort";
import { InfraFilters } from "@/pages/NetworkOdc";
import { ServerFilter } from "@/components/network/ServerFilter";
import { OtbFormDialog } from "@/components/network/OtbFormDialog";
import { OtbDetail } from "@/components/network/OtbDetail";
import { StatusPill, LocCell, inServerText } from "@/components/network/StatusPill";
import { DeleteInfraButton } from "@/components/network/DeleteInfraButton";
import { OldKodeLabel } from "@/components/network/OldKodeLabel";
import { OtbCoreSummary } from "@/components/network/OtbCoreSummary";
import { ArchiveInfraButton } from "@/components/network/ArchiveInfraButton";
import { useNetworkRefs, SELECT_CLS } from "@/lib/network";

// Tampilan HP (< md): kartu ringkas; urutan memakai state sort tabel yang sama.
function OtbCards({ rows, st, cols, q, wilayahName, me, canManage, onView, onEdit, onChanged, empty }) {
  const val = st.sort ? `${st.sort.col}:${st.sort.dir}` : "";
  return (
    <div className="space-y-2 md:hidden" data-testid="otb-cards">
      <select className={`${SELECT_CLS} w-full`} value={val} data-testid="otb-cards-sort"
              onChange={(e) => { const [col, dir] = e.target.value.split(":"); st.setSort(col ? { col, dir } : null); }}>
        <option value="">Urutan default</option>
        {cols.flatMap(([c, l]) => [<option key={`${c}a`} value={`${c}:asc`}>{l} ↑</option>, <option key={`${c}d`} value={`${c}:desc`}>{l} ↓</option>])}
      </select>
      {(rows || []).map((o) => (
        <div key={o.id} className="rounded-lg border border-[#E5E7EB] bg-white p-3" data-testid={`otb-card-${o.kode}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="break-all font-mono-tab text-sm font-semibold">{o.kode}<OldKodeLabel row={o} q={q} testid={`otb-card-oldkode-${o.kode}`} />
                {o.archived && <span className="ml-1 rounded bg-[#E5E7EB] px-1 text-[10px] font-semibold text-[#374151]" data-testid={`otb-card-archived-${o.kode}`}>ARSIP</span>}</div>
              <div className="text-xs text-[#374151]">{o.nama}</div>
              <div className={`text-xs ${o.wilayah_valid === false ? "text-[#B45309]" : "text-[#6B7280]"}`}>{wilayahName(o.wilayah_id)}{o.in_server ? ` · ${inServerText(o.in_server)}` : !o.has_coords && " · belum ada lokasi peta"}</div>
            </div>
            <StatusPill status={o.status} />
          </div>
          <div className="mt-2 flex flex-wrap items-end justify-between gap-2">
            <div data-testid={`otb-card-core-${o.kode}`}><OtbCoreSummary compact core={o.core} testid={`otb-card-core-sum-${o.kode}`} />
              <div className="text-xs text-[#6B7280]">{o.cable_count} kabel terhubung</div></div>
            <div className="flex shrink-0 items-center">
              <Button size="icon" variant="ghost" onClick={() => onView(o.id)} data-testid={`otb-card-view-${o.kode}`}><Eye className="h-4 w-4" /></Button>
              <ArchiveInfraButton iconOnly kind="otb" item={o} me={me} onDone={onChanged} tid={`otb-card-${o.archived ? "restore" : "archive"}-${o.kode}`} />
              {canManage && !o.archived && <Button size="icon" variant="ghost" onClick={() => onEdit(o)} data-testid={`otb-card-edit-${o.kode}`}><Pencil className="h-4 w-4" /></Button>}
              <DeleteInfraButton iconOnly kind="otb" item={o} me={me} onDeleted={onChanged} tid={`otb-card-delete-${o.kode}`} />
            </div>
          </div>
        </div>
      ))}
      {empty && <p className="rounded-lg border border-[#E5E7EB] bg-white p-6 text-center text-sm text-[#6B7280]" data-testid="otb-cards-empty">{empty}</p>}
    </div>
  );
}

export default function NetworkOtb() {
  const { refs, wilayahName } = useNetworkRefs();
  const [f, setF] = useState({ q: "", lokasi_id: "", wilayah_id: "", status: "", archived: false, server_id: "" });
  const [all, setAll] = useState(null);
  const [over, setOver] = useState(false);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => api.get("/network/otb", { params: f }).then((r) => setAll(r.data)), [f]);
  const rows = useMemo(() => (all && over ? all.filter((o) => o.core?.kabel_melebihi_kapasitas) : all), [all, over]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const getters = useMemo(() => ({ kode: (o) => o.kode, nama: (o) => o.nama, core: (o) => o.jumlah_core, kabel: (o) => o.cable_count,
    wilayah: (o) => wilayahName(o.wilayah_id), status: (o) => o.status }), [wilayahName]);
  const st = useTableSort("otb", rows, getters);
  const canManage = !!refs?.me?.can_manage;
  const emptyText = `${over ? "Tidak ada OTB dengan kabel melebihi kapasitas untuk filter ini" : f.archived ? "Tidak ada OTB yang diarsipkan" : `Belum ada OTB${Object.values(f).some(Boolean) ? " untuk filter ini" : ""}`}.`;
  const cols = [["kode", "Kode"], ["nama", "Nama"], ["core", "Core"], ["kabel", "Kabel"], ["wilayah", "Wilayah"], ["status", "Status"]];
  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="network-otb-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold">Master OTB</h1>
          <p className="text-sm text-[#6B7280]">Optical Termination Box — terminasi kabel feeder{canManage ? "" : " (mode lihat)"}</p></div>
        {canManage && <Button onClick={() => setForm({})} data-testid="otb-add-btn"><Plus className="mr-1 h-4 w-4" />Tambah OTB</Button>}
      </div>
      <InfraFilters refs={refs} f={f} setF={setF} prefix="otb" placeholder="Cari kode, kode lama, nama, alamat…">
        <label className="flex items-center gap-1.5 text-xs" data-testid="otb-filter-archived">
          <input type="checkbox" checked={f.archived} onChange={(e) => setF((x) => ({ ...x, archived: e.target.checked }))} data-testid="otb-filter-archived-toggle" />Tampilkan Arsip</label>
        <label className="flex items-center gap-1.5 text-xs" data-testid="otb-filter-over">
          <input type="checkbox" checked={over} onChange={(e) => setOver(e.target.checked)} data-testid="otb-filter-over-toggle" />Hanya yang melebihi kapasitas</label>
        <ServerFilter f={f} setF={setF} prefix="otb" />
      </InfraFilters>
      <OtbCards rows={st.sorted} st={st} cols={cols} q={f.q} wilayahName={wilayahName} me={refs?.me} canManage={canManage}
                onView={setDetail} onEdit={setForm} onChanged={load} empty={rows && !rows.length ? emptyText : null} />
      <div className="hidden overflow-x-auto rounded-lg border border-[#E5E7EB] bg-white md:block">
        <table className="w-full min-w-[760px] text-sm" data-testid="otb-table">
          <thead className="bg-[#F8F9FA] text-left text-xs uppercase text-[#6B7280]">
            <tr>{cols.map(([c, l], i) => <SortTh key={c} className={i ? "" : "p-2"} col={c} label={l} s={st} testid="otb" />)}<th>Peta</th><th className="pr-2 text-right">Aksi</th></tr>
          </thead>
          <tbody>
            {(st.sorted || []).map((o) => (
              <tr key={o.id} className="border-t" data-testid={`otb-row-${o.kode}`}>
                <td className="p-2 font-mono-tab font-semibold">{o.kode}<OldKodeLabel row={o} q={f.q} testid={`otb-oldkode-${o.kode}`} />{o.archived && <span className="ml-1 rounded bg-[#E5E7EB] px-1 text-[10px] font-semibold text-[#374151]" data-testid={`otb-archived-badge-${o.kode}`}>ARSIP</span>}</td>
                <td>{o.nama}</td><td data-testid={`otb-core-${o.kode}`}><OtbCoreSummary compact core={o.core} testid={`otb-core-sum-${o.kode}`} /></td><td>{o.cable_count}</td>
                <td className={o.wilayah_valid === false ? "text-[#B45309]" : ""}>{wilayahName(o.wilayah_id)}</td>
                <td><StatusPill status={o.status} /></td>
                <td><LocCell row={o} testid={`otb-nocoord-${o.kode}`} /></td>
                <td className="whitespace-nowrap pr-2 text-right">
                  <Button size="icon" variant="ghost" onClick={() => setDetail(o.id)} data-testid={`otb-view-${o.kode}`}><Eye className="h-4 w-4" /></Button>
                  <ArchiveInfraButton iconOnly kind="otb" item={o} me={refs?.me} onDone={load} />
                  {canManage && !o.archived && <Button size="icon" variant="ghost" onClick={() => setForm(o)} data-testid={`otb-edit-${o.kode}`}><Pencil className="h-4 w-4" /></Button>}
                  <DeleteInfraButton iconOnly kind="otb" item={o} me={refs?.me} onDeleted={load} />
                </td>
              </tr>
            ))}
            {rows && !rows.length && <tr><td colSpan={8} className="p-8 text-center text-[#6B7280]" data-testid="otb-empty">
              <Network className="mx-auto mb-2 h-8 w-8 opacity-40" />{emptyText}</td></tr>}
          </tbody>
        </table>
      </div>
      <OtbFormDialog open={!!form} editing={form?.id ? form : null} refs={refs} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[92dvh] max-w-2xl overflow-y-auto" data-testid="otb-detail-dialog">
          <DialogHeader><DialogTitle>Detail OTB</DialogTitle></DialogHeader>
          {detail && <OtbDetail otbId={detail} me={refs?.me} wilayahName={wilayahName} onDeleted={() => { setDetail(null); load(); }} onArchived={() => { setDetail(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
